import React, { useMemo, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useQuery, keepPreviousData } from "@tanstack/react-query";
import { ArrowDown, ArrowUp, ArrowUpDown, Download, FileSpreadsheet, Inbox, Loader2, Search, X } from "lucide-react";
import { toast } from "sonner";
import { api, downloadFile, errorMessage } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { useDirectory, useMeta } from "@/hooks/useMeta";
import { fmtDateTime } from "@/lib/format";
import { StatusBadge } from "@/components/dashboard/StatusBadge";
import { Btn, EmptyState, LabeledSelect, PageHeader, Pagination } from "@/components/dashboard/Ui";
import { SubmissionDetailSheet } from "@/components/dashboard/SubmissionDetailSheet";

const COLUMNS = [
  { key: "created_at", label: "Received" },
  { key: "beneficiary_name", label: "Beneficiary" },
  { key: "contact_name", label: "Contact" },
  { key: "phone", label: "Phone" },
  { key: "service", label: "Service" },
  { key: "assessment_type", label: "Assessment" },
  { key: "referral_source", label: "Referral" },
  { key: "preferred_contact_time", label: "Contact time" },
  { key: "language", label: "Lang" },
  { key: "status", label: "Status" },
  { key: "assigned_admin_name", label: "Assigned" },
  { key: "appointment_start", label: "Appointment" },
];

export default function SubmissionsPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const { isSupervisor } = useAuth();
  const { data: meta } = useMeta();
  const { data: directory } = useDirectory();
  const [exporting, setExporting] = useState(null);

  const filters = useMemo(
    () => ({
      q: params.get("q") || "",
      status: params.get("status") || "",
      service: params.get("service") || "",
      specialty: params.get("specialty") || "",
      assigned_admin_id: params.get("assigned") || "",
      date_from: params.get("from") || "",
      date_to: params.get("to") || "",
      sort_by: params.get("sort") || "created_at",
      sort_dir: params.get("dir") || "desc",
      page: Number(params.get("page") || 1),
      include_deleted: params.get("trash") === "1",
    }),
    [params]
  );

  const setFilter = (patch) => {
    const next = new URLSearchParams(params);
    Object.entries(patch).forEach(([k, v]) => {
      if (v === "" || v === null || v === undefined || v === false) next.delete(k);
      else next.set(k, String(v));
    });
    if (!("page" in patch)) next.delete("page");
    setParams(next, { replace: true });
  };

  const queryParams = {
    q: filters.q || undefined,
    status: filters.status || undefined,
    service: filters.service || undefined,
    specialty: filters.specialty || undefined,
    assigned_admin_id: filters.assigned_admin_id || undefined,
    date_from: filters.date_from || undefined,
    date_to: filters.date_to || undefined,
    sort_by: filters.sort_by,
    sort_dir: filters.sort_dir,
    include_deleted: filters.include_deleted || undefined,
  };

  const { data, isLoading, isFetching } = useQuery({
    queryKey: ["submissions", queryParams, filters.page],
    queryFn: async () => (await api.get("/submissions", { params: { ...queryParams, page: filters.page, page_size: 25 } })).data,
    placeholderData: keepPreviousData,
  });

  const toggleSort = (key) => {
    if (filters.sort_by === key) setFilter({ sort: key, dir: filters.sort_dir === "asc" ? "desc" : "asc" });
    else setFilter({ sort: key, dir: key === "created_at" || key === "appointment_start" ? "desc" : "asc" });
  };

  const onExport = async (fmt) => {
    setExporting(fmt);
    try {
      await downloadFile("/submissions/export", { ...queryParams, fmt }, `emerge-submissions.${fmt}`);
      toast.success(`${fmt.toUpperCase()} export ready`);
    } catch (e) {
      toast.error(errorMessage(e, "Export failed"));
    } finally {
      setExporting(null);
    }
  };

  const hasFilters = filters.q || filters.status || filters.service || filters.specialty || filters.assigned_admin_id || filters.date_from || filters.date_to || filters.include_deleted;
  const label = (dict, key) => (key ? meta?.[dict]?.[key] || key : "");
  const specialties = meta?.specialties || {};
  // A submission carries a service; its colour comes from the specialty that
  // would handle it, so the table matches the Team and Schedule colour key.
  const specialtyOfService = (service) => meta?.service_to_specialty?.[service] || null;
  const serviceColor = (service) => specialties[specialtyOfService(service)]?.color || null;
  const admins = (directory || []).filter((u) => u.status === "active" || u.status === "inactive");

  return (
    <div data-testid="submissions-page">
      <PageHeader
        title="Submissions"
        description={isSupervisor ? "Every intake request, newest first. Click a row to open the full record." : "New requests and the ones assigned to you. Click a row to open the full record."}
        actions={
          <>
            <Btn variant="secondary" onClick={() => onExport("csv")} disabled={!!exporting} data-testid="submissions-export-csv-button">
              {exporting === "csv" ? <Loader2 className="animate-spin" /> : <Download />} CSV
            </Btn>
            <Btn variant="secondary" onClick={() => onExport("xlsx")} disabled={!!exporting} data-testid="submissions-export-xlsx-button">
              {exporting === "xlsx" ? <Loader2 className="animate-spin" /> : <FileSpreadsheet />} Excel
            </Btn>
          </>
        }
      />

      {/* Filter bar */}
      <div className="emerge-card mb-4 grid gap-3 p-4 md:grid-cols-[1.4fr_repeat(3,1fr)] xl:grid-cols-[1.6fr_repeat(5,1fr)]">
        <div className="relative">
          <Search className="pointer-events-none absolute start-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <input className="emerge-input !h-10 ps-9 text-sm" placeholder="Search name, phone, notes..." value={filters.q} onChange={(e) => setFilter({ q: e.target.value })} data-testid="submissions-search-input" aria-label="Search submissions" />
        </div>
        <LabeledSelect id="f-status" value={filters.status} onChange={(e) => setFilter({ status: e.target.value })} data-testid="submissions-filter-status-select" aria-label="Filter by status">
          <option value="">All statuses</option>
          {(meta?.statuses || []).map((s) => (
            <option key={s} value={s}>
              {meta.status_labels[s]}
            </option>
          ))}
        </LabeledSelect>
        <LabeledSelect id="f-service" value={filters.service} onChange={(e) => setFilter({ service: e.target.value })} data-testid="submissions-filter-service-select" aria-label="Filter by service">
          <option value="">All services</option>
          {Object.entries(meta?.services || {}).map(([k, v]) => (
            <option key={k} value={k}>
              {v}
            </option>
          ))}
        </LabeledSelect>
        <LabeledSelect id="f-specialty" value={filters.specialty} onChange={(e) => setFilter({ specialty: e.target.value })} data-testid="submissions-filter-specialty-select" aria-label="Filter by specialty">
          <option value="">All specialties</option>
          {Object.entries(specialties).map(([k, v]) => (
            <option key={k} value={k}>{v.label}</option>
          ))}
        </LabeledSelect>
        <LabeledSelect id="f-assigned" value={filters.assigned_admin_id} onChange={(e) => setFilter({ assigned: e.target.value })} data-testid="submissions-filter-admin-select" aria-label="Filter by assigned admin">
          <option value="">Any admin</option>
          <option value="unassigned">Unassigned</option>
          {admins.map((a) => (
            <option key={a.id} value={a.id}>
              {a.name}
            </option>
          ))}
        </LabeledSelect>
        <input type="date" className="emerge-input !h-10 text-sm" value={filters.date_from} onChange={(e) => setFilter({ from: e.target.value })} aria-label="From date" data-testid="submissions-filter-from-input" />
        <input type="date" className="emerge-input !h-10 text-sm" value={filters.date_to} onChange={(e) => setFilter({ to: e.target.value })} aria-label="To date" data-testid="submissions-filter-to-input" />
        <div className="flex items-center justify-between gap-3 md:col-span-4 xl:col-span-6">
          <div className="flex items-center gap-4 text-sm text-muted-foreground">
            <span data-testid="submissions-count">{isFetching && !isLoading ? "Updating..." : `${data?.total ?? 0} submission${data?.total === 1 ? "" : "s"}`}</span>
            {isSupervisor && (
              <label className="inline-flex items-center gap-2">
                <input type="checkbox" className="h-4 w-4 accent-[#2f5d83]" checked={filters.include_deleted} onChange={(e) => setFilter({ trash: e.target.checked ? "1" : "" })} data-testid="submissions-include-trash-checkbox" />
                Include trash
              </label>
            )}
          </div>
          {hasFilters && (
            <Btn variant="ghost" size="sm" onClick={() => setParams({}, { replace: true })} data-testid="submissions-clear-filters-button">
              <X /> Clear filters
            </Btn>
          )}
        </div>
      </div>

      {/* Table */}
      <div className="emerge-card overflow-hidden">
        {isLoading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="h-6 w-6 animate-spin text-primary" />
          </div>
        ) : (data?.items || []).length === 0 ? (
          <EmptyState icon={Inbox} title="No submissions match" description={hasFilters ? "Try clearing some filters." : "New intake requests will appear here as soon as clients submit the form."} testId="submissions-empty" />
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full min-w-[1100px] text-sm" data-testid="submissions-table">
              <thead className="sticky top-0 bg-secondary/70 text-start text-xs uppercase tracking-wide text-muted-foreground">
                <tr>
                  {COLUMNS.map((c) => {
                    const active = filters.sort_by === c.key;
                    return (
                      <th key={c.key} scope="col" className="px-3 py-3 text-start font-semibold">
                        <button type="button" className="inline-flex items-center gap-1 hover:text-foreground" onClick={() => toggleSort(c.key)} data-testid={`submissions-sort-${c.key}`} aria-sort={active ? (filters.sort_dir === "asc" ? "ascending" : "descending") : "none"}>
                          {c.label}
                          {active ? filters.sort_dir === "asc" ? <ArrowUp className="h-3.5 w-3.5" /> : <ArrowDown className="h-3.5 w-3.5" /> : <ArrowUpDown className="h-3.5 w-3.5 opacity-40" />}
                        </button>
                      </th>
                    );
                  })}
                </tr>
              </thead>
              <tbody>
                {data.items.map((s) => (
                  <tr
                    key={s.id}
                    tabIndex={0}
                    role="button"
                    onClick={() => navigate(`/dashboard/submissions/${s.id}${window.location.search}`)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        navigate(`/dashboard/submissions/${s.id}${window.location.search}`);
                      }
                    }}
                    className={`table-row-hover cursor-pointer border-t border-border focus-visible:bg-secondary ${s.deleted_at ? "opacity-60" : ""}`}
                    data-testid="submissions-row"
                  >
                    <td className="whitespace-nowrap px-3 py-3 text-muted-foreground">{fmtDateTime(s.created_at)}</td>
                    <td className="px-3 py-3 font-semibold text-foreground">{s.beneficiary_name}</td>
                    <td className="px-3 py-3">{s.contact_name}</td>
                    <td className="whitespace-nowrap px-3 py-3" dir="ltr">
                      {s.phone}
                    </td>
                    <td className="px-3 py-3">
                      {serviceColor(s.service) ? (
                        <span
                          className="inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-semibold"
                          style={{ backgroundColor: `${serviceColor(s.service)}1A`, color: serviceColor(s.service) }}
                          data-testid="submission-specialty-chip"
                        >
                          <span className="h-2 w-2 rounded-full" style={{ backgroundColor: serviceColor(s.service) }} />
                          {label("services", s.service)}
                        </span>
                      ) : (
                        label("services", s.service)
                      )}
                      {s.service_other ? <span className="block text-xs text-muted-foreground">{s.service_other}</span> : null}
                    </td>
                    <td className="px-3 py-3 text-muted-foreground">
                      {label("assessment_types", s.assessment_type) || "-"}
                      {s.assessment_other ? <span className="block text-xs">{s.assessment_other}</span> : null}
                    </td>
                    <td className="px-3 py-3">
                      {label("referral_sources", s.referral_source)}
                      {s.referral_other ? <span className="block text-xs text-muted-foreground">{s.referral_other}</span> : null}
                    </td>
                    <td className="whitespace-nowrap px-3 py-3">
                      {label("contact_times", s.preferred_contact_time)}
                      {s.preferred_time_specific ? ` ${s.preferred_time_specific}` : ""}
                    </td>
                    <td className="px-3 py-3 uppercase text-muted-foreground">{s.language}</td>
                    <td className="px-3 py-3">
                      <StatusBadge status={s.status} />
                    </td>
                    <td className="px-3 py-3">{s.assigned_admin_name || <span className="text-muted-foreground">-</span>}</td>
                    <td className="whitespace-nowrap px-3 py-3">{s.appointment_start ? fmtDateTime(s.appointment_start) : <span className="text-muted-foreground">-</span>}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        {data && <Pagination page={filters.page} pageSize={data.page_size} total={data.total} onChange={(p) => setFilter({ page: p })} />}
      </div>

      {id && <SubmissionDetailSheet id={id} onClose={() => navigate(`/dashboard/submissions${window.location.search}`)} />}
    </div>
  );
}
